import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orderService } from '../services/order.service';
import { OrderItem, OrderStatus, Payment } from '../types';
import { Address } from '../types/order.types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { formatCurrency } from '../utils/currency';
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  MapPin,
  CreditCard,
  Receipt,
  Banknote,
  CircleDot,
  ShoppingBag,
  CalendarDays,
  Hash,
  Copy,
  Check,
} from 'lucide-react';

const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  NEW: { label: 'New', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10', icon: CircleDot },
  PENDING: { label: 'Pending', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500/10', icon: Clock },
  PROCESSING: { label: 'Processing', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-500/10', icon: Package },
  SHIPPED: { label: 'Shipped', color: 'text-sky-600 dark:text-sky-400', bgColor: 'bg-sky-500/10', icon: Truck },
  DELIVERED: { label: 'Delivered', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10', icon: CheckCircle },
  CANCELLED: { label: 'Cancelled', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-500/10', icon: XCircle },
};

const PAYMENT_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  PENDING: { label: 'Pending', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500/10' },
  SUCCESS: { label: 'Paid', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10' },
  paid: { label: 'Paid', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10' },
  FAILED: { label: 'Failed', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-500/10' },
  failed: { label: 'Failed', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-500/10' },
};

const ORDER_STEPS: OrderStatus[] = ['NEW', 'PENDING', 'PROCESSING', 'SHIPPED', 'DELIVERED'];

function getStepIndex(status: OrderStatus): number {
  if (status === 'CANCELLED') return -1;
  return ORDER_STEPS.indexOf(status);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatProvider(provider?: string): string {
  if (!provider) return 'Online Payment';
  const map: Record<string, string> = {
    cashfree: 'Cashfree',
    razorpay: 'Razorpay',
    stripe: 'Stripe',
    paypal: 'PayPal',
    cash_on_delivery: 'Cash on Delivery',
    cod: 'Cash on Delivery',
    manual: 'Manual',
  };
  return map[provider] || provider;
}

function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="inline-flex items-center gap-1.5 group cursor-pointer" title="Copy ID">
      <span className="font-mono text-xs text-muted-foreground truncate max-w-[180px] sm:max-w-[280px]">{value}</span>
      {copied ? <Check className="size-3 text-emerald-500" /> : <Copy className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />}
    </button>
  );
}

function AddressBlock({ label, address, icon }: { label: string; address?: Address | null; icon: React.ReactNode }) {
  if (!address) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="text-sm leading-relaxed text-foreground">
        <p>{address.line1}</p>
        {address.line2 && <p>{address.line2}</p>}
        <p>{address.city}, {address.state} {address.postal_code}</p>
        <p>{address.country}</p>
      </div>
    </div>
  );
}

function PaymentSection({ payment, currency }: { payment?: Payment | null; currency: string }) {
  if (!payment) return null;

  const statusCfg = PAYMENT_STATUS_CONFIG[payment.status] || { label: payment.status, color: 'text-muted-foreground', bgColor: 'bg-muted' };
  const isCod = payment.provider === 'cash_on_delivery' || payment.provider === 'cod';

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <CreditCard className="size-4 text-muted-foreground" />
        <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Payment</h3>
      </div>
      <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isCod ? <Banknote className="size-4 text-muted-foreground" /> : <CreditCard className="size-4 text-muted-foreground" />}
            <span className="text-sm font-semibold">{formatProvider(payment.provider)}</span>
          </div>
          <Badge className={`${statusCfg.bgColor} ${statusCfg.color} border-0 font-bold text-[10px] uppercase tracking-wider`}>
            {statusCfg.label}
          </Badge>
        </div>

        <Separator />

        <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
          <div className="text-muted-foreground">Amount</div>
          <div className="text-right font-semibold">{formatCurrency(payment.amount, payment.currency || currency)}</div>

          {payment.payment_method && (
            <>
              <div className="text-muted-foreground">Method</div>
              <div className="text-right font-semibold capitalize">{payment.payment_method.replace(/_/g, ' ')}</div>
            </>
          )}

          {payment.provider_order_id && (
            <>
              <div className="text-muted-foreground">Provider Ref</div>
              <div className="text-right">
                <CopyableId value={payment.provider_order_id} />
              </div>
            </>
          )}

          <div className="text-muted-foreground">Paid At</div>
          <div className="text-right font-semibold text-xs">{formatDateTime(payment.updated_at)}</div>
        </div>
      </div>
    </section>
  );
}

export const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: orderData, isLoading: loading, error } = useQuery({
    queryKey: ['order', id],
    queryFn: () => orderService.getOrder(id || ''),
    enabled: !!id,
  });

  const order = orderData?.order;
  const items = orderData?.items;
  const payment = orderData?.payment;

  if (loading) return <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 flex justify-center"><LoadingSpinner /></div>;

  if (error || !order) return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 text-center space-y-4">
      <h2 className="text-2xl font-black text-destructive tracking-tight uppercase">Order Not Found</h2>
      <p className="text-muted-foreground font-medium">This order does not exist or may have been removed.</p>
      <Button asChild className="rounded-xl">
        <Link to="/orders">Return to Orders</Link>
      </Button>
    </div>
  );

  const statusCfg = STATUS_CONFIG[order.status];
  const StatusIcon = statusCfg.icon;
  const currentStep = getStepIndex(order.status);
  const isCancelled = order.status === 'CANCELLED';
  const curr = order.currency || 'USD';

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      {/* Back Navigation */}
      <Button variant="ghost" size="sm" className="pl-0 hover:bg-transparent group mb-6" asChild>
        <Link to="/orders" className="flex items-center text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="mr-1 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-xs font-bold uppercase tracking-wider">Back to Orders</span>
        </Link>
      </Button>

      {/* Header */}
      <header className="mb-10 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`size-10 rounded-xl ${statusCfg.bgColor} flex items-center justify-center`}>
              <StatusIcon className={`size-5 ${statusCfg.color}`} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-foreground uppercase leading-none">Order Details</h1>
              <div className="flex items-center gap-2 mt-1">
                <Hash className="size-3 text-muted-foreground" />
                <CopyableId value={order.id} />
              </div>
            </div>
          </div>
          <Badge className={`${statusCfg.bgColor} ${statusCfg.color} border-0 font-bold text-xs uppercase tracking-wider px-4 py-1.5 self-start`}>
            {statusCfg.label}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground font-medium">
          <span className="flex items-center gap-1.5"><CalendarDays className="size-3" /> Placed {formatDateTime(order.created_at)}</span>
          {order.updated_at !== order.created_at && (
            <span className="flex items-center gap-1.5"><Clock className="size-3" /> Updated {formatDateTime(order.updated_at)}</span>
          )}
          {order.customer_email && (
            <span className="flex items-center gap-1.5"><ShoppingBag className="size-3" /> {order.customer_email}</span>
          )}
        </div>
      </header>

      {/* Order Progress Tracker */}
      {!isCancelled && (
        <section className="mb-10 bg-card border border-border rounded-2xl p-6 md:p-8">
          <div className="flex items-center justify-between relative">
            {/* Progress line */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-border mx-8" />
            <div
              className="absolute top-5 left-0 h-0.5 bg-primary mx-8 transition-all duration-500"
              style={{ width: `${currentStep >= 0 ? (currentStep / (ORDER_STEPS.length - 1)) * 100 : 0}%` }}
            />

            {ORDER_STEPS.map((step, i) => {
              const cfg = STATUS_CONFIG[step];
              const StepIcon = cfg.icon;
              const isCompleted = currentStep >= i;
              const isCurrent = currentStep === i;
              return (
                <div key={step} className="flex flex-col items-center gap-2 relative z-10">
                  <div className={`size-10 rounded-full flex items-center justify-center transition-all ${
                    isCurrent ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' :
                    isCompleted ? 'bg-primary text-primary-foreground' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    <StepIcon className="size-4" />
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-widest hidden sm:block ${
                    isCompleted ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {cfg.label}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Cancelled Banner */}
      {isCancelled && (
        <div className="mb-10 bg-red-500/5 border border-red-500/20 rounded-2xl p-6 flex items-center gap-4">
          <div className="size-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <XCircle className="size-5 text-red-500" />
          </div>
          <div>
            <h3 className="font-bold text-red-600 dark:text-red-400">Order Cancelled</h3>
            <p className="text-sm text-muted-foreground">This order was cancelled on {formatDate(order.updated_at)}.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
        {/* Main Content */}
        <div className="lg:col-span-8 space-y-8">
          {/* Items */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="size-4 text-muted-foreground" />
                <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  Items ({items?.length || 0})
                </h2>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              {/* Table header */}
              <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 bg-muted/50 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <div className="col-span-5">Product</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-1 text-center">Qty</div>
                <div className="col-span-2 text-right">Tax</div>
                <div className="col-span-2 text-right">Total</div>
              </div>

              {items?.map((item: OrderItem, idx: number) => (
                <div key={item.id} className={`grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-6 py-4 items-center ${
                  idx < (items.length - 1) ? 'border-b border-border' : ''
                }`}>
                  <div className="sm:col-span-5 flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-primary/5 flex items-center justify-center flex-shrink-0">
                      <Package className="size-4 text-primary/40" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">{item.name}</h3>
                      {item.sku && <p className="text-[10px] text-muted-foreground font-medium">SKU: {item.sku}</p>}
                    </div>
                  </div>
                  <div className="sm:col-span-2 text-right text-sm text-muted-foreground">
                    <span className="sm:hidden text-[10px] font-bold uppercase tracking-widest mr-2">Price:</span>
                    {formatCurrency(item.unit_price, curr)}
                  </div>
                  <div className="sm:col-span-1 text-center text-sm font-semibold">
                    <span className="sm:hidden text-[10px] font-bold uppercase tracking-widest text-muted-foreground mr-2">Qty:</span>
                    {item.quantity}
                  </div>
                  <div className="sm:col-span-2 text-right text-sm text-muted-foreground">
                    <span className="sm:hidden text-[10px] font-bold uppercase tracking-widest mr-2">Tax:</span>
                    {formatCurrency(item.tax_amount, curr)}
                  </div>
                  <div className="sm:col-span-2 text-right text-sm font-bold text-foreground">
                    <span className="sm:hidden text-[10px] font-bold uppercase tracking-widest text-muted-foreground mr-2">Total:</span>
                    {formatCurrency(item.total_amount, curr)}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Addresses */}
          {(order.shipping_address || order.billing_address) && (
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <MapPin className="size-4 text-muted-foreground" />
                <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Addresses</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {order.shipping_address && (
                  <div className="bg-card border border-border rounded-2xl p-5">
                    <AddressBlock
                      label="Shipping"
                      address={order.shipping_address}
                      icon={<Truck className="size-3" />}
                    />
                  </div>
                )}
                {order.billing_address && (
                  <div className="bg-card border border-border rounded-2xl p-5">
                    <AddressBlock
                      label="Billing"
                      address={order.billing_address}
                      icon={<Receipt className="size-3" />}
                    />
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Payment Details */}
          <PaymentSection payment={payment} currency={curr} />
        </div>

        {/* Sidebar — Order Summary */}
        <div className="lg:col-span-4">
          <div className="bg-[#111418] text-white rounded-2xl p-6 md:p-8 space-y-6 sticky top-24 shadow-2xl">
            <h2 className="text-lg font-black tracking-tight uppercase flex items-center gap-2">
              <Receipt className="size-4 text-white/60" /> Order Summary
            </h2>

            {/* Customer Info */}
            {order.customer_name && (
              <div className="space-y-1 pb-4 border-b border-white/10">
                <p className="text-xs text-white/40 font-bold uppercase tracking-widest">Customer</p>
                <p className="text-sm font-semibold">{order.customer_name}</p>
                {order.customer_email && <p className="text-xs text-white/60">{order.customer_email}</p>}
              </div>
            )}

            {/* Price Breakdown */}
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-white/50">Subtotal</span>
                <span className="font-semibold">{formatCurrency(order.subtotal_amount, curr)}</span>
              </div>

              {order.tax_amount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/50">Tax</span>
                  <span className="font-semibold">{formatCurrency(order.tax_amount, curr)}</span>
                </div>
              )}

              {order.shipping_amount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/50">Shipping</span>
                  <span className="font-semibold">{formatCurrency(order.shipping_amount, curr)}</span>
                </div>
              )}
              {order.shipping_amount === 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/50">Shipping</span>
                  <span className="text-emerald-400 text-xs font-bold uppercase tracking-wider">Free</span>
                </div>
              )}

              {order.discount_amount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/50">Discount</span>
                  <span className="text-emerald-400 font-semibold">-{formatCurrency(order.discount_amount, curr)}</span>
                </div>
              )}

              <Separator className="bg-white/10" />

              <div className="flex justify-between items-center pt-1">
                <span className="text-base font-black uppercase tracking-tight">Total</span>
                <span className="text-xl font-black text-primary">{formatCurrency(order.total_amount, curr)}</span>
              </div>
            </div>

            {/* Payment Status Summary */}
            {payment && (
              <div className="pt-4 border-t border-white/10 space-y-3">
                <p className="text-xs text-white/40 font-bold uppercase tracking-widest">Payment</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {payment.provider === 'cash_on_delivery' || payment.provider === 'cod'
                      ? <Banknote className="size-4 text-white/50" />
                      : <CreditCard className="size-4 text-white/50" />}
                    <span className="text-sm font-medium">{formatProvider(payment.provider)}</span>
                  </div>
                  <Badge className={`${
                    payment.status === 'SUCCESS' || payment.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                    payment.status === 'FAILED' || payment.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                    'bg-amber-500/20 text-amber-400'
                  } border-0 font-bold text-[10px] uppercase tracking-wider`}>
                    {PAYMENT_STATUS_CONFIG[payment.status]?.label || payment.status}
                  </Badge>
                </div>
              </div>
            )}

            {/* Order Meta */}
            <div className="pt-4 border-t border-white/10 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Order Date</span>
                <span className="text-white/70 font-medium">{formatDate(order.created_at)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Last Updated</span>
                <span className="text-white/70 font-medium">{formatDate(order.updated_at)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Currency</span>
                <span className="text-white/70 font-medium">{curr}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-2 space-y-3">
              <Button asChild variant="outline" className="w-full rounded-xl border-white/10 text-white hover:bg-white/10 hover:text-white">
                <Link to="/orders">View All Orders</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
